import React, { useEffect, useState, useRef } from 'react';
import { useRunAnalysisStore } from '@/run-stage/store/runAnalysisStore';
import { CommunicationBridge } from '@services/communicationBridge.service';
import { authStore } from '@services/authStore.service';
import type { InitPayload, ErrorPayload } from '@/types/communication.types';
import { RunAnalysisHeader } from './AnalysisStage/RunAnalysisHeader';
import { RunAnalysisGraph } from './AnalysisStage/RunAnalysisGraph';
import { RunAnalysisSidebar } from './AnalysisStage/RunAnalysisSidebar';
import './RunAnalysisPage.css';

/**
 * RunAnalysisPage
 *
 * Main container for the v2 Run Analysis experience.
 * Handles INIT message from Angular, fetches analysis data,
 * and orchestrates graph + sidebar tabs (Protocol, Comments, Info).
 */
export const RunAnalysisPage: React.FC = () => {
  const initRan = useRef(false);
  const [componentInit, setComponentInit] = useState<{
    analysisId: string;
    entityType: string;
  } | null>(null);

  const { analysis, status, error, hydrate, clear } = useRunAnalysisStore();

  // Listen for INIT message from Angular host
  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;

    // Send READY immediately to signal React app is mounted
    CommunicationBridge.sendReady();

    const handleInit = (msg: InitPayload) => {
      const { entityId, entityType, authToken } = msg;

      
      if (entityType !== 'RUN_ANALYSIS') {
        const errorPayload: ErrorPayload = {
          code: 'INIT_ERROR',
          message: `Invalid entity type: ${entityType}. Expected RUN_ANALYSIS.`,
        };
        CommunicationBridge.sendError(errorPayload);
        return;
      }

      // Set auth token in store for API calls
      if (authToken) {
        authStore.setToken(authToken);
        }

      setComponentInit({ analysisId: entityId, entityType });
    };

    CommunicationBridge.on('INIT', handleInit);

    return () => {
      CommunicationBridge.off('INIT', handleInit);
    };
  }, []);

  // Hydrate store when componentInit is set
  useEffect(() => {
    if (!componentInit) return;

    const performHydration = async () => {
      try {
        await hydrate(componentInit.analysisId);
        } catch (err) {
        console.error('[RunAnalysisPage] Hydration error:', err);
        const errorPayload: ErrorPayload = {
          code: 'HYDRATION_ERROR',
          message: err instanceof Error ? err.message : 'Failed to load analysis',
          details: err,
        };
        CommunicationBridge.sendError(errorPayload);
      }
    };

    performHydration();
  }, [componentInit, hydrate]);

  // Handle loading/error states
  if (!componentInit) {
    return (
      <div className="run-analysis-page loading">
        <p>Initializing analysis viewer...</p>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="run-analysis-page loading">
        <p>Loading analysis...</p>
      </div>
    );
  }

  if (status === 'error' || !analysis) {
    return (
      <div className="run-analysis-page error">
        <div className="error-container">
          <h2>Unable to Load Analysis</h2>
          <p>{error || 'An unknown error occurred'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="run-analysis-page">
      <RunAnalysisHeader analysis={analysis} />
      <div className="run-analysis-content">
        <RunAnalysisGraph analysis={analysis} />
        <RunAnalysisSidebar analysis={analysis} />
      </div>
    </div>
  );
};

export default RunAnalysisPage;
